import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
  Box,
  Code,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
} from '@chakra-ui/react';
import { useToast, CloseCircleOutline } from '@aslanpay/portal-common-lib';

import { useState } from 'react';

import TextWithClipboard from 'app/components/UI/TextWithClipboard';
import useCreateAppClient from 'app/hooks/useCreateAppClient';

import { ScimInstructions } from '../ScimInstructions';

import { AddAppClientModalProps, AppClientCredentials } from './AddAppClientModal.types';

function AddAppClientModal({
  companyId = '',
  isOpen,
  onClose,
  onSuccess = () => {},
}: AddAppClientModalProps) {
  const [credentials, setCredentials] = useState<AppClientCredentials | null>(null);
  const toast = useToast();
  const createAppClientMutation = useCreateAppClient();

  const handleConfirmAdd = async () => {
    if (!companyId) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Invalid input',
        description: 'Company ID is required.',
      });
      return;
    }

    try {
      const result = await createAppClientMutation.mutateAsync({
        companyId,
      });

      // Store credentials to display them
      setCredentials({
        clientId: result.clientId,
        clientSecret: result.clientSecret,
      });

      onSuccess?.();
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the app client was not added. Please try again.',
      });
    }
  };

  const handleClose = () => {
    setCredentials(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={credentials ? '4xl' : 'lg'}
      isCentered
      closeOnOverlayClick={!credentials}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      {/* The shared Modal theme sets dialog maxWidth: 'unset', which overrides the `size`/`maxW`
          props; an inline style is needed to actually cap the dialog width. */}
      <ModalContent style={credentials ? { maxWidth: 'min(720px, 90vw)' } : undefined}>
        <ModalHeader>
          <HStack>
            <Text as="h6">{credentials ? 'App Client Created' : 'Create App Client'}</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close add app client modal"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          {credentials ? (
            <VStack spacing="20px" align="stretch">
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  <strong>Warning:</strong> Copy these credentials now. The client secret will not
                  be shown again.
                </AlertDescription>
              </Alert>

              <Box>
                <Text fontWeight="semibold" mb="8px">
                  Client ID:
                </Text>
                <Code
                  p="12px"
                  borderRadius="md"
                  display="block"
                  fontFamily="Inconsolata, monospace"
                  fontSize="sm"
                  wordBreak="break-all"
                >
                  <TextWithClipboard value={credentials.clientId} truncate={false} />
                </Code>
              </Box>

              <Box>
                <Text fontWeight="semibold" mb="8px">
                  Client Secret:
                </Text>
                <Code
                  p="12px"
                  borderRadius="md"
                  display="block"
                  fontFamily="Inconsolata, monospace"
                  fontSize="sm"
                  wordBreak="break-all"
                  bg="red.50"
                >
                  <TextWithClipboard value={credentials.clientSecret} truncate={false} />
                </Code>
              </Box>

              <Divider />

              <ScimInstructions
                clientId={credentials.clientId}
                clientSecret={credentials.clientSecret}
              />
            </VStack>
          ) : (
            <VStack spacing="12px">
              <Text color="brand.mutedPurple" textStyle="body">
                Are you sure you want to create a new Cognito app client?
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {credentials ? (
            <Button onClick={handleClose}>Close</Button>
          ) : (
            <HStack spacing="10px">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleConfirmAdd} isLoading={createAppClientMutation.isPending}>
                Create
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { AddAppClientModal };

export default AddAppClientModal;
